/**
 * This file is specifically for making simple Ajax calls and cache individual objects from response collection in localStorage.
 *
 * Parameters for makeHttpRequest method:
 *   {
 *       method: String (optional),
 *       url: String,
 *       params: String | Object (optional),
 *       headers: Object (optional),
 *       storageKey: String (optional)
 *       callbackFunction: function reference to be executed after successful response with API result.
 *   }
 *
 * Usage:
 *     import makeHttpRequest from '../helpers/ajax-cache-collection-response';
 *     var request = {url: ___, method: ___, storageKey: ___}
 *     makeHttpRequest(request).then(function(response) {
 *           ......
 *      }, function(error){
 *          .......
 *      });
 */
import appStorage from './app-storage';

const makeHttpRequest = (options) => {
    const expiration = options.expireTime ? options.expireTime : 60 * 60 * 1000;

    // Make an XMLHttpRequest and return a promise
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(options.method || 'GET', options.url);
        xhr.onload = () => {
            const successData = xhr.responseText;
            if (successData) {
                const jsonData = JSON.parse(successData);
                if (jsonData.success) {
                    const result = jsonData.result;
                    if (options.storageKey) {
                        for (let i = 0; i < result.length; i++) {
                            const storageKey = `${options.storageKey}_${result[i].lenderId}`;
                            appStorage.set(storageKey, result[i], expiration);
                        }
                        if (options && options.callbackFunction) {
                            options.callbackFunction(result);
                        }
                    }
                }
            }
        };

        xhr.onerror = () => {
            const failureData = { status: this.status, statusText: xhr.statusText };
            reject(JSON.stringify(failureData));
        };

        if (options.headers) {
            Object.keys(options.headers).forEach((key) => {
                xhr.setRequestHeader(key, options.headers[key]);
            });
        }

        if (options.method === 'POST') {
            xhr.setRequestHeader('Content-Type', 'application/json');
            xhr.send(JSON.stringify(options.params));
        } else {
            let { params } = options;
            if (params && typeof params === 'object') {
                params = Object.keys(params).map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`).join('&');
            }
            xhr.send(params);
        }
    });
};

export default makeHttpRequest;
